import React, { useEffect, useState } from "react";
import {
  showErrorToaster as showErrorToastImport,
  showLocalizedToaster,
} from "../State/actions/toaster";
import { connect } from "react-redux";
import surveysService from "../services/surveysService";
import contentLibraryService from "../services/contentLibraryService";
import ArticlePreview from "../Shared/ContentLibrary/Preview/ArticlePreview";
import SurveyRenderer from "../Shared/Surveys/renderer/SurveyRenderer";
import ModalPopup from "../components/ModalPopup";
import MPSBarLoader from "../components/MPSBarLoader";
import Messages from "../Shared/Messages";
import history from "../history";
import { isPatientOrMember } from "../security/roles";

const REDIRECT_ROUTES = {
  patient: {
    library: (id) => `/app/h/info?content-id=${id}`,
    survey: (id) => `/app/surveys/${id}`,
  },
  staff: {
    library: (id) => `/information?content-id=${id}`,
    survey: (id) => `/surveys/${id}/edit`,
  },
};

const contentInitialState = () => ({
  name: "",
  response: null,
  Component: () => null,
});

const HTMLFormatter = (props) => {
  const { children, showToaster, showErrorToaster, patientId, userRole, themeColor } = props;
  const [content, setContent] = useState(contentInitialState());
  const [contentLoading, setContentLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const isPatientContext = isPatientOrMember(userRole);
  const routeKind = isPatientContext ? "patient" : "staff";

  const redirectToContent = async (contentType, contentName) => {
    try {
      if (contentType === "library") {
        const library = await contentLibraryService.loadLibraryByName(
          contentName,
          patientId
        );
        if (library && library.id) {
          history.push(REDIRECT_ROUTES[routeKind].library(library.id));
          return;
        }
      } else if (contentType === "survey") {
        const survey = await surveysService.loadSurveyByName(contentName);
        if (survey && survey.id) {
          history.push(REDIRECT_ROUTES[routeKind].survey(survey.id));
          return;
        }
      }
      showToaster({ content: Messages.error_loading_data }, "error");
    } catch (err) {
      showToaster({ content: Messages.error_loading_data }, "error");
    }
  };

  const submitAnswers = async (responseData) => {
    let statusId = null;

    setContentLoading(true);
    return surveysService
      .submitAnswers(statusId, responseData, patientId)
      .then((response) => {
        showToaster({ content: Messages.survey_submitted }, "success");
      })
      .catch((err) => {
        if (err.message) {
          showErrorToaster(err, err.message);
        } else {
          showToaster({ content: Messages.error }, "error");
        }
        console.log(err);
      })
      .finally(() => setContentLoading(false));
  };

  const showContent = async (contentType, contentName) => {
    let name = "";
    let response = null;
    let Component = null;
    setContentLoading(true);
    setShowModal(true);
    switch (contentType) {
      case "library":
        name = "Content Library";
        response = await contentLibraryService.loadLibraryByName(contentName);
        Component = () => <ArticlePreview article={response} embed />;
        break;
      case "survey":
        name = "Survey";
        response = await surveysService.loadSurveyByName(contentName);
        Component = () =>
          response && (
            <SurveyRenderer
              survey={response}
              submitAnswers={submitAnswers}
              previewMode={!patientId}
              embedded
            />
          );
        break;

      default:
        break;
    }
    setContentLoading(false);
    setContent({ name, response, Component });
  };

  const prepareButtons = () => {
    const buttons = document.querySelectorAll(
      'a[href^="https://goto.mypatientspace.com/"]'
    );
    if (buttons.length) {
      Array.from(buttons).forEach((button) => {
        const contentType = button.href.startsWith(
          "https://goto.mypatientspace.com/l"
        )
          ? "library"
          : button.href.startsWith("https://goto.mypatientspace.com/s")
            ? "survey"
            : button.className.split(" ")[1];
        const url = new URL(button.href);
        const contentName = url.searchParams.get("name");
        const action = url.searchParams.get("action") || "modal";
        const isAnchorLayout = !button.classList.contains("content-cta");
        button.href = "#";
        button.target = "_self";
        button.className = isAnchorLayout
          ? "formatted-content-link"
          : "formatted-content-button";
        if (themeColor) {
          button.style.setProperty("--mps-content-button-color", themeColor);
        }
        if (isAnchorLayout) {
          if (themeColor) {
            button.style.color = themeColor;
          }
        } else {
          if (themeColor) {
            button.style.backgroundColor = themeColor;
            button.style.borderColor = themeColor;
          }
          button.style.color = "#fff";
        }
        button.addEventListener("click", (e) => {
          e.preventDefault();
          if (!contentName || contentName === "null") {
            showToaster({ content: Messages.error_loading_data }, "error");
            return;
          }
          if (action === "redirect") {
            redirectToContent(contentType, contentName);
          } else {
            showContent(contentType, contentName);
          }
        });
      });
    }
  };

  const onCloseModal = () => {
    setShowModal(false);
    setContent(contentInitialState());
  };

  useEffect(() => {
    prepareButtons();
  }, [prepareButtons]);

  return (
    <>
      {children}
      {showModal && (
        <ModalPopup title={content.name} onClose={onCloseModal}>
          {contentLoading ? (
            <div style={{ margin: "100px auto", width: 100 }}>
              <MPSBarLoader />
            </div>
          ) : (
            <content.Component />
          )}
        </ModalPopup>
      )}
    </>
  );
};

const mapStateToProps = (state, ownProps, _) => {
  const patientId =
    ownProps.patientId ||
    ownProps.match?.params?.id ||
    state.user?.data?.patientId;
  const rawThemeColor = state.user?.data?.appTheme?.themeColor;
  return {
    patientId,
    userRole: state.user?.role,
    themeColor: rawThemeColor ? rawThemeColor.replace("0x", "#") : "",
  };
};

const mapDispatchToProps = {
  showToaster: showLocalizedToaster,
  showErrorToaster: showErrorToastImport,
};

export default connect(mapStateToProps, mapDispatchToProps)(HTMLFormatter);
